import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  Signal,
  inject,
  input,
  viewChild,
  InputSignal,
} from '@angular/core';
import {
  ModalController,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonIcon,
  IonFooter,
} from '@ionic/angular/standalone';
import { register, SwiperContainer } from 'swiper/element/bundle';
import { ApplicationFacade } from '@lyceo/shared/data-access';
import { WalkthroughImages } from '@lyceo/shared/walkthrough/util';
import { toObservable } from '@angular/core/rxjs-interop';
import { addIcons } from 'ionicons';
import { chevronBackOutline, chevronForwardOutline } from 'ionicons/icons';
import { ButtonComponent } from '@lyceo/shared/ui';
import { SwiperOptions } from 'swiper/types';

@Component({
  selector: 'lyceo-walkthrough-modal',
  templateUrl: './walkthrough-modal.component.html',
  styleUrls: ['./walkthrough-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonIcon,
    IonFooter,
    ButtonComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class WalkthroughModalComponent implements AfterViewInit {
  private readonly modalController: ModalController = inject(ModalController);
  private readonly applicationFacade: ApplicationFacade =
    inject(ApplicationFacade);

  protected readonly swiperRef: Signal<ElementRef<SwiperContainer>> =
    viewChild.required<ElementRef<SwiperContainer>>('swiper');

  public readonly walkthroughImages: InputSignal<WalkthroughImages> =
    input.required<WalkthroughImages>();
  public readonly walkthroughTitle: InputSignal<string> =
    input.required<string>();

  public isMobileScreenSize: Signal<boolean | undefined> =
    this.applicationFacade.isMobileScreenSize;

  public images: Signal<string[]> = computed(() => {
    return this.isMobileScreenSize()
      ? this.walkthroughImages().mobile
      : this.walkthroughImages().desktop;
  });

  constructor() {
    toObservable(this.images).subscribe(() => this.updateSwiper());
    addIcons({ chevronBackOutline, chevronForwardOutline });
  }

  public ngAfterViewInit(): void {
    register();

    this.initSwiper();
  }

  public close(): void {
    this.modalController.dismiss();
  }

  private initSwiper(): void {
    const swiperContainer: SwiperContainer = this.swiperRef().nativeElement;
    const swiperOptions: SwiperOptions = {
      slidesPerView: 1,
      speed: 300,
      navigation: {
        nextEl: '.button-next',
        prevEl: '.button-prev',
      },
      centeredSlides: true,
      pagination: {
        clickable: true,
      },
      keyboard: {
        enabled: true,
      },
    };
    if (swiperContainer) {
      Object.assign(swiperContainer, swiperOptions);

      swiperContainer.initialize();
    }
  }

  private updateSwiper(): void {
    const swiperRef: ElementRef<SwiperContainer> = this.swiperRef();
    if (
      swiperRef &&
      swiperRef.nativeElement &&
      swiperRef.nativeElement.swiper
    ) {
      swiperRef.nativeElement.swiper.update();
      swiperRef.nativeElement.swiper.slideTo(0, 0, false);
    }
  }
}
